<template>
  <section class="invoice-detail-wrapper">
    <b-card no-body>
      <b-card-header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <h2>{{ $t("accounting.patient-invoices") }}</h2>
          <b-button variant="outline-secondary" @click="$router.go(-1)">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
            {{ $t("common.back") }}
          </b-button>
        </div>
      </b-card-header>

      <b-card-body v-if="loading">
        <div class="d-flex justify-content-center">
          <b-spinner label="Loading..." />
        </div>
      </b-card-body>

      <b-card-body v-else-if="patientData">
        <!-- Patient Information -->
        <b-card class="mb-3">
          <h4 class="mb-3 text-primary">
            {{ $t("common.patient_information") }}
          </h4>
          <b-row>
            <b-col md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("common.name") }}:</span
                >
                <span class="text-primary">{{ patientData.name || "-" }}</span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("accounting.patient_no") }}:</span
                >
                <span class="text-primary">{{
                  patientData.patient_no || "-"
                }}</span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("reception.phone") }}:</span
                >
                <span
                  class="text-primary"
                  :style="{
                    direction: $store.state.appConfig.isRTL ? 'rtl' : 'ltr',
                  }"
                >
                  {{ patientData.phone || "-" }}
                </span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("reception.birth-date") }}:</span
                >
                <span class="text-primary">{{
                  patientData.birth_date || "-"
                }}</span>
              </div>
            </b-col>
            <b-col md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("reception.gender") }}:</span
                >
                <span class="text-primary">{{
                  patientData.gender || "-"
                }}</span>
              </div>
            </b-col>
            <b-col v-if="patientData.patient_card" md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("common.card_no") }}:</span
                >
                <span class="text-primary">{{
                  patientData.patient_card.card_no || "-"
                }}</span>
              </div>
            </b-col>
            <b-col v-if="patientData.patient_card" md="3">
              <div class="d-flex mb-2 align-items-center">
                <span class="font-weight-bold mr-2"
                  >{{ $t("common.status") }}:</span
                >
                <b-badge
                  :variant="
                    patientData.patient_card.is_active ? 'success' : 'danger'
                  "
                >
                  {{
                    patientData.patient_card.is_active ? "Active" : "Not Active"
                  }}
                </b-badge>
              </div>
            </b-col>
          </b-row>
        </b-card>

        <!-- Invoice Sections -->
        <!-- Out Clinic Invoices -->
        <b-card
          v-if="
            patientData.invoice_out_clinic &&
            patientData.invoice_out_clinic.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("hospital.outclinic-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_out_clinic"
            responsive
            :fields="outClinicColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(service_title)="data">
              <div>{{ data.item.service ? data.item.service.title : "-" }}</div>
            </template>
            <template #cell(amount)="data">
              <div>{{ data.item.amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="outClinicInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
            <template #cell(updated_at)="data">
              <div>{{ formatDateTime(data.item.updated_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- Surgery Deposit Invoices -->
        <b-card
          v-if="
            patientData.invoice_insurance_surgery &&
            patientData.invoice_insurance_surgery.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("hospital.surgery-deposit-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_insurance_surgery"
            responsive
            :fields="surgeryDepositColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(amount)="data">
              <div>{{ data.item.amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="surgeryDepositInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
            <template #cell(updated_at)="data">
              <div>{{ formatDateTime(data.item.updated_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- Patient Surgery Invoices -->
        <b-card
          v-if="
            patientData.invoice_patient_surgery &&
            patientData.invoice_patient_surgery.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("hospital.patient-surgery-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_patient_surgery"
            responsive
            :fields="patientSurgeryColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(total_amount)="data">
              <div>{{ data.item.total_amount }}</div>
            </template>
            <template #cell(hospital_net_amount)="data">
              <div>{{ data.item.hospital_net_amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="patientSurgeryInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- Patient Stay Invoices -->
        <b-card
          v-if="
            patientData.invoice_patient_stay &&
            patientData.invoice_patient_stay.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("common.patient-stay-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_patient_stay"
            responsive
            :fields="patientStayColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(total_amount)="data">
              <div>{{ data.item.total_amount }}</div>
            </template>
            <template #cell(hospital_net_amount)="data">
              <div>{{ data.item.hospital_net_amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="patientStayInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- NICU Invoices -->
        <b-card
          v-if="patientData.invoice_nicu && patientData.invoice_nicu.length > 0"
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("incubator.nicu-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_nicu"
            responsive
            :fields="nicuColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(amount)="data">
              <div>{{ data.item.amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="nicuInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- Emergency Invoices -->
        <b-card
          v-if="
            patientData.invoice_emergency &&
            patientData.invoice_emergency.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("emergency.emergency-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_emergency"
            responsive
            :fields="emergencyColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(amount)="data">
              <div>{{ data.item.amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="emergencyInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- Test Invoices -->
        <b-card
          v-if="
            patientData.invoice_patient_card_test &&
            patientData.invoice_patient_card_test.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("lab.test-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_patient_card_test"
            responsive
            :fields="testColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(amount)="data">
              <div>{{ data.item.amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="testInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- Refund Invoices -->
        <b-card
          v-if="
            patientData.invoice_refund && patientData.invoice_refund.length > 0
          "
          class="mb-3"
        >
          <h5 class="mb-3">
            {{ $t("accounting.refund-invoices") }}
          </h5>
          <b-table
            :items="patientData.invoice_refund"
            responsive
            :fields="refundColumns"
            primary-key="id"
            class="position-relative border-bottom"
            show-empty
            striped
            hover
            empty-text="No invoices found"
          >
            <template #cell(amount)="data">
              <div>{{ data.item.amount }}</div>
            </template>
            <template #cell(status)="data">
              <ChangeInvoiceStatusDropDown
                name="refundInvoice"
                endpoint="invoices"
                :data="data.item"
                @onEmit="fetchPatientInvoices"
              />
            </template>
            <template #cell(date)="data">
              <div>
                {{ data.item.date || formatDateTime(data.item.created_at) }}
              </div>
            </template>
            <template #cell(created_at)="data">
              <div>{{ formatDateTime(data.item.created_at) }}</div>
            </template>
          </b-table>
        </b-card>

        <!-- No Invoices Message -->
        <b-alert
          v-if="
            (!patientData.invoice_out_clinic ||
              patientData.invoice_out_clinic.length === 0) &&
            (!patientData.invoice_insurance_surgery ||
              patientData.invoice_insurance_surgery.length === 0) &&
            (!patientData.invoice_patient_surgery ||
              patientData.invoice_patient_surgery.length === 0) &&
            (!patientData.invoice_patient_stay ||
              patientData.invoice_patient_stay.length === 0) &&
            (!patientData.invoice_nicu ||
              patientData.invoice_nicu.length === 0) &&
            (!patientData.invoice_emergency ||
              patientData.invoice_emergency.length === 0) &&
            (!patientData.invoice_patient_card_test ||
              patientData.invoice_patient_card_test.length === 0) &&
            (!patientData.invoice_refund ||
              patientData.invoice_refund.length === 0)
          "
          variant="info"
          show
        >
          {{ $t("accounting.no-invoices-found") }}
        </b-alert>
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BBadge,
  BSpinner,
  BAlert,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ChangeInvoiceStatusDropDown from "@/components/invoices/ChangeInvoiceStatusDropDown.vue";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BBadge,
    BSpinner,
    BAlert,
    ChangeInvoiceStatusDropDown,
  },
  data() {
    return {
      loading: false,
      patientData: null,
      outClinicColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        {
          key: "service_title",
          label: this.$t("hospital.service"),
          sortable: true,
        },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      surgeryDepositColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      patientSurgeryColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        {
          key: "total_amount",
          label: this.$t("accounting.total_amount"),
          sortable: true,
        },
        {
          key: "hospital_net_amount",
          label: this.$t("accounting.hospital_net_amount"),
          sortable: true,
        },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      patientStayColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        {
          key: "total_amount",
          label: this.$t("accounting.total_amount"),
          sortable: true,
        },
        {
          key: "hospital_net_amount",
          label: this.$t("accounting.hospital_net_amount"),
          sortable: true,
        },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      nicuColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      emergencyColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      testColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
      refundColumns: [
        {
          key: "invoice_no",
          label: this.$t("accounting.invoice_no"),
          sortable: true,
        },
        { key: "amount", label: this.$t("accounting.amount"), sortable: true },
        { key: "status", label: this.$t("accounting.status"), sortable: true },
        { key: "date", label: this.$t("common.date"), sortable: true },
        {
          key: "created_at",
          label: this.$t("common.created-at"),
          sortable: true,
        },
      ],
    };
  },
  mounted() {
    this.fetchPatientInvoices();
  },
  methods: {
    async fetchPatientInvoices() {
      this.loading = true;
      try {
        await this.getPatient(
          `/patient/invoices?filter[id]=${this.$route.params.patientId}&include=patientCard&sort=-id`
        );

        // The API returns data in all_patient_data (from patient store)
        // Handle both array and object responses
        const responseData = this.all_patient_data || this.all_data;
        if (responseData) {
          if (Array.isArray(responseData)) {
            this.patientData = responseData.length > 0 ? responseData[0] : null;
          } else if (responseData.data && Array.isArray(responseData.data)) {
            this.patientData =
              responseData.data.length > 0 ? responseData.data[0] : null;
          } else if (
            responseData.data &&
            typeof responseData.data === "object"
          ) {
            this.patientData = responseData.data;
          } else {
            this.patientData = responseData;
          }
        } else {
          this.patientData = null;
        }
      } catch (error) {
        console.error("Error fetching patient invoices:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-fetching-data"),
          },
        });
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "";
      const date = new Date(dateTimeStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getStatusVariant(status) {
      if (!status) return "secondary";
      switch (status.toLowerCase()) {
        case "approved":
          return "success";
        case "pending":
          return "warning";
        case "rejected":
          return "danger";
        case "printed":
          return "info";
        case "paid":
          return "success";
        default:
          return "secondary";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.invoice-detail-wrapper {
  .card {
    margin-bottom: 1rem;
  }

  .table {
    th {
      font-weight: 600;
      background-color: #f8f8f8;
    }
  }
}
</style>
